import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Alert, Anchor, Button, Group, Text } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { fetchWithTimeout, getApiBase, isNativeApp, rememberApiBackup } from '../api/client';
import { nativePlatform } from '../api/platform';
import { hasUnsavedWork } from '../unsaved-work';

/**
 * Старая сборка — и на планшете, и в браузере.
 *
 * НА ПЛАНШЕТЕ APK ставится руками, файлом. Обновить забывают, и заметить
 * это неоткуда: экран выглядит так же, а считает по прошлогодним
 * формулам. Замер при этом уходит на сервер и выглядит нормальным.
 *
 * В БРАУЗЕРЕ ПРОГРАММА ОБНОВЛЯЕТСЯ САМА (правило 211). Сборку держит
 * service worker, и открытая вкладка живёт на вчерашних файлах, пока её
 * не перезагрузят, — в офисе вкладку не закрывают целый день. Раньше здесь
 * стояла кнопка «Обновить», и владелец назвал это прямо: «почему вообще веб
 * версия обновляется по кнопке, что за бред». Кнопка стояла не зря:
 * перезагрузка посреди набранной формы стирает набранное молча
 * (правило 187). Теперь вкладка перезагружается сама, но только в
 * безопасный момент: при переходе на другой экран (его и так покидают), в
 * скрытой вкладке или после минуты без движения — и никогда поверх
 * набранного и не сохранённого.
 *
 * НА ПЛАНШЕТЕ И В НАСТОЛЬНОЙ ПРОГРАММЕ обновить себя нечем: сборка лежит
 * внутри них, и перезагрузка её не сменит. Там текст говорит, что делать.
 */

/**
 * Как часто спрашивать сервер. Десять минут: ответ крошечный, а вкладка
 * узнаёт о выкатке, пока человек ещё за ней.
 */
const CHECK_INTERVAL_MS = 10 * 60 * 1000;

/** Сколько простоя считать «человек отошёл»: ни нажатий, ни прокрутки */
export const IDLE_MS = 60_000;

/**
 * Настольная программа отдаёт файлы ИЗ СЕБЯ.
 *
 * Electron поднимает свой сервер и раздаёт сборку, упакованную внутрь
 * (`resources/web`). Перезагрузка страницы там не приносит ничего
 * нового — сколько ни перезагружай, файлы те же, — и кнопка «Обновить»
 * оказывалась обещанием, которого программа выполнить не может. Ровно
 * та беда, от которой правило 19 уводит: кнопка, всегда отвечающая
 * ничем, хуже отсутствующей.
 */
const isDesktopApp = () =>
  typeof navigator !== 'undefined' && /electron/i.test(navigator.userAgent);

/**
 * Можно ли вкладке перезагрузиться прямо сейчас (правило 211).
 *
 * Переход на другой экран — всегда: прежний экран и так покидают, и всё,
 * что на нём не сохранено, уходит вместе с ним. Набранное и не
 * сохранённое — никогда: пропажа формы молчит, и замечают её, когда
 * набирать заново уже поздно (правило 187). Иначе — в скрытой вкладке (на
 * неё не смотрят) или после минуты без движения.
 */
export function safeToReload(s: { navigated: boolean; dirty: boolean; hidden: boolean; idleMs: number }): boolean {
  if (s.navigated) return true;
  if (s.dirty) return false;
  return s.hidden || s.idleMs >= IDLE_MS;
}

/**
 * Дождаться, пока новый service worker возьмёт страницу.
 *
 * `registration.update()` только СПРАШИВАЕТ сервер; новый worker после
 * этого ещё устанавливается. Перезагрузка, сделанная сразу, успевает
 * раньше и получает ту же старую сборку из старого кэша — со стороны
 * человека это «обновилось, а ничего не поменялось». Ждём смены
 * управляющего worker'а, но не бесконечно: не сменился за шесть секунд —
 * перезагружаемся всё равно, хуже не будет.
 */
function waitForNewWorker(ms = 6000): Promise<void> {
  return new Promise((resolve) => {
    if (!('serviceWorker' in navigator)) return resolve();
    const sw = navigator.serviceWorker;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      sw.removeEventListener('controllerchange', finish);
      resolve();
    };
    sw.addEventListener('controllerchange', finish);
    setTimeout(finish, ms);
  });
}

/**
 * Что уже пробовали: мягкий путь (новый worker и перезагрузка) и жёсткий
 * (снять worker, почистить кэши). Ключи живут до закрытия вкладки и
 * помнят ВЕРСИЮ сервера, для которой пробовали: следующая выкатка
 * начинает снова с мягкого.
 */
const TRIED_KEY = 'blagodar.updateTried';
const HARD_KEY = 'blagodar.updateHard';
const readKey = (key: string) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};
const writeKey = (key: string, v: string) => {
  try {
    sessionStorage.setItem(key, v);
  } catch {
    // Приватное окно — переживём: просто пойдём мягким путём ещё раз
  }
};

/**
 * Версия работающего кода — из страницы, с которой он загрузился
 * (`<meta name="app-version">`, см. `vite.config.ts`). В код она не
 * зашивается: зашитая, она меняла общий файл сборки, а за ним имена всех
 * остальных, и каждая выкатка перекачивала всю программу на каждое
 * устройство. Проверкам подставляют глобальную `__APP_VERSION__`.
 */
function runningVersion(): string | null {
  const fromPage = document.querySelector('meta[name="app-version"]')?.getAttribute('content')?.trim();
  if (fromPage) return fromPage;
  return typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : null;
}

/**
 * Забрать свежую сборку.
 *
 * Просто перезагрузить страницу мало: файлы отдаёт service worker из
 * своего кэша. Сперва МЯГКИЙ путь: спросить сервер и дождаться смены
 * worker'а — если он уже сменился, ждать нечего. Если после него сборка
 * всё та же — ЖЁСТКИЙ: worker снимается, кэши чистятся, страница грузится
 * с нуля. Так же чинится и застрявший кэш в APK (правило 29), только там
 * это делается само.
 */
async function reloadToNewBuild(serverVersion: string, workerSwitched: boolean): Promise<void> {
  const hard = readKey(TRIED_KEY) === serverVersion;
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (hard) {
        writeKey(HARD_KEY, serverVersion);
        await Promise.all(registrations.map((r) => r.unregister().catch(() => undefined)));
        if ('caches' in window) {
          const keys = await caches.keys().catch(() => [] as string[]);
          await Promise.all(keys.map((k) => caches.delete(k).catch(() => undefined)));
        }
      } else {
        writeKey(TRIED_KEY, serverVersion);
        if (!workerSwitched) {
          await Promise.all(registrations.map((r) => r.update().catch(() => undefined)));
          await waitForNewWorker();
        }
      }
    }
  } finally {
    window.location.reload();
  }
}

export function VersionNotice() {
  const [serverVersion, setServerVersion] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [updating, setUpdating] = useState(false);
  /** Адрес свежего APK на сервере — если сервер его раздаёт. */
  const [apkUrl, setApkUrl] = useState<string | null>(null);

  const check = useCallback(async () => {
    try {
      const response = await fetchWithTimeout(`${getApiBase()}/health`);
      if (!response.ok) return;
      const data = (await response.json()) as { version?: string; backupApi?: string | null };
      if (data.version) setServerVersion(data.version);
      // Запасной вход (правило 238) запоминается, пока основной отвечает:
      // когда он понадобится, спросить будет не у кого
      rememberApiBackup(data.backupApi);
    } catch {
      // Нет связи — не повод пугать человека: он и так это видит
      // по индикатору синхронизации
    }

    // Кнопка «Скачать обновление» появляется только после проверки, что
    // файл действительно выложен: кнопка, которая всегда отвечает
    // ошибкой, хуже отсутствующей (правило 19). Адрес API на планшете
    // полный — APK лежит на том же сервере рядом с /api
    // На iPhone APK ни к чему: программу обновляет App Store
    if (nativePlatform() !== 'android') return;
    const origin = getApiBase().replace(/\/api\/?$/, '');
    if (!/^https?:\/\//i.test(origin)) return;
    try {
      const head = await fetchWithTimeout(`${origin}/app.apk`, { method: 'HEAD' });
      setApkUrl(head.ok ? `${origin}/app.apk` : null);
    } catch {
      setApkUrl(null);
    }
  }, []);

  useEffect(() => {
    void check();
    const timer = window.setInterval(() => void check(), CHECK_INTERVAL_MS);

    // Возврат к вкладке — самый частый момент, когда расхождение уже
    // случилось: развернули, пока человек обедал
    const onFocus = () => void check();
    window.addEventListener('focus', onFocus);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [check]);

  const appVersion = runningVersion();
  /**
   * Сравниваются только версии СО СБОРОЧНОЙ ОТМЕТКОЙ.
   *
   * Номер в `package.json` между сборками не меняется, и отличает их
   * отметка времени — `0.1.0+202609020410` (правило 30). Сервер, не
   * знающий своей сборки, отвечает голым `0.1.0`: так бывает на
   * стенде разработчика и на службе, запущенной руками без
   * `APP_VERSION`. Строки не совпадут НИКОГДА, и вкладка обновлялась бы
   * по кругу, а плашка на планшете горела бы вечно — на всех разом и
   * сразу после установки свежего APK.
   *
   * Ложная тревога хуже её отсутствия: на неё перестают смотреть, и
   * настоящее расхождение проходит незамеченным (правило 61). Поэтому
   * без отметки хотя бы у одной стороны программа МОЛЧИТ — сказать ей
   * нечего, а выдумывать нельзя (правило 88).
   */
  const stamped = (v: string | null) => !!v && /\+\d{6,}/.test(v);
  const outdated = stamped(serverVersion) && stamped(appVersion) && serverVersion !== appVersion;
  const inBrowser = !isNativeApp() && !isDesktopApp();
  // Сама пробовала обоими путями, а сборка всё та же: дальше — рукой
  const gaveUp = outdated && inBrowser && readKey(HARD_KEY) === serverVersion;
  const autoUpdate = outdated && inBrowser && !gaveUp;

  /*
   * ЧТО НАБРАНО И НЕ СОХРАНЕНО. Любой ввод в поле — несохранённое, пока
   * человек не ушёл с экрана или пока сохранение не ушло ПОСЛЕ последнего
   * нажатия: сохранили то, что набрали. Прокрутка и нажатия — движение:
   * по ним считается простой.
   */
  const dirty = useRef(false);
  const lastInputAt = useRef(0);
  const lastActivityAt = useRef(Date.now());
  const workerSwitched = useRef(false);
  const reloading = useRef(false);
  // Обработчики живут всё время экрана и читают свежее из ссылок; ссылки
  // обновляются после отрисовки — писать в них во время отрисовки React
  // не велит
  const autoRef = useRef(autoUpdate);
  const serverRef = useRef(serverVersion);
  useLayoutEffect(() => {
    autoRef.current = autoUpdate;
    serverRef.current = serverVersion;
  });

  const tryReload = useCallback((navigated: boolean) => {
    if (!autoRef.current || reloading.current || !serverRef.current) return;
    const ok = safeToReload({
      navigated,
      // Холст набирается касаниями, а не вводом — он отмечается сам
      dirty: dirty.current || hasUnsavedWork(),
      hidden: typeof document !== 'undefined' && document.visibilityState === 'hidden',
      idleMs: Date.now() - lastActivityAt.current,
    });
    if (!ok) return;
    reloading.current = true;
    void reloadToNewBuild(serverRef.current, workerSwitched.current);
  }, []);

  useEffect(() => {
    const onInput = () => {
      dirty.current = true;
      lastInputAt.current = Date.now();
      lastActivityAt.current = Date.now();
    };
    const onActivity = () => {
      lastActivityAt.current = Date.now();
    };
    const onVisibility = () => tryReload(false);
    const activity = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;
    document.addEventListener('input', onInput, true);
    document.addEventListener('change', onInput, true);
    for (const name of activity) document.addEventListener(name, onActivity, { capture: true, passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    const timer = window.setInterval(() => tryReload(false), 5000);
    return () => {
      document.removeEventListener('input', onInput, true);
      document.removeEventListener('change', onInput, true);
      for (const name of activity) document.removeEventListener(name, onActivity, true);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(timer);
    };
  }, [tryReload]);

  // Сохранение, ушедшее ПОСЛЕ последнего нажатия, — сохранило набранное
  const queryClient = useQueryClient();
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (event?.type !== 'updated') return;
        const state = event.mutation.state;
        if (state.status === 'success' && state.submittedAt >= lastInputAt.current) dirty.current = false;
      }),
    [queryClient],
  );

  // Переход на другой экран — лучший момент: прежний и так покидают
  const location = useLocation();
  const route = `${location.pathname}${location.search}`;
  const lastRoute = useRef(route);
  useEffect(() => {
    if (route === lastRoute.current) return;
    lastRoute.current = route;
    tryReload(true);
    dirty.current = false;
  }, [route, tryReload]);

  /*
   * НОВАЯ СБОРКА ГОТОВИТСЯ ЗАРАНЕЕ: worker забирает её в фоне, пока
   * человек работает, — страница при этом не перезагружается (регистрация
   * в `main.tsx` этого не делает). Когда безопасный момент придёт,
   * перезагрузка возьмёт уже скачанное и займёт мгновение.
   */
  useEffect(() => {
    if (!autoUpdate || !('serviceWorker' in navigator)) return;
    const sw = navigator.serviceWorker;
    const onSwitch = () => {
      workerSwitched.current = true;
    };
    sw.addEventListener('controllerchange', onSwitch);
    void sw
      .getRegistrations()
      .then((registrations) => Promise.all(registrations.map((r) => r.update().catch(() => undefined))))
      .catch(() => undefined);
    // Вкладка уже скрыта или человек давно отошёл — ждать нечего
    tryReload(false);
    return () => sw.removeEventListener('controllerchange', onSwitch);
  }, [autoUpdate, serverVersion, tryReload]);

  if (autoUpdate) {
    // Человеку тут делать нечего: обновится само. Метка — для проверок
    return <span hidden data-testid="update-pending" />;
  }

  if (!outdated || dismissed) return null;

  // iPhone обновляется из App Store или TestFlight: файла, который можно
  // скачать с нашего сервера, у него нет и быть не может
  if (nativePlatform() === 'ios') {
    return (
      <Alert
        color="orange"
        icon={<IconRefresh size={18} />}
        radius="md"
        mb="md"
        withCloseButton
        onClose={() => setDismissed(true)}
        title="На iPhone старая версия программы"
        data-testid="ios-outdated"
      >
        <Text size="sm">
          Здесь {appVersion}, на сервере {serverVersion}. Расчёты могли поменяться — обновите
          «БлагоДар» в App Store (или в TestFlight, если ставили оттуда).
        </Text>
        <Text size="sm" mt={4}>
          Незавершённые замеры и расходки при обновлении сохраняются: они лежат в памяти телефона.
        </Text>
        <Anchor size="sm" mt={4} onClick={() => setDismissed(true)}>
          Напомнить позже
        </Anchor>
      </Alert>
    );
  }

  if (isNativeApp()) {
    return (
      <Alert
        color="orange"
        icon={<IconRefresh size={18} />}
        radius="md"
        mb="md"
        withCloseButton
        onClose={() => setDismissed(true)}
        title="На планшете старая версия программы"
      >
        {apkUrl ? (
          // Сервер раздаёт свежий APK сам — человек с кабелем больше не
          // нужен. Обычная ссылка: WebView передаёт скачивание системному
          // браузеру (DownloadListener в MainActivity), файл открывают из
          // загрузок, и Android ставит его поверх
          <>
            <Text size="sm">
              Здесь {appVersion}, на сервере {serverVersion}. Расчёты могли поменяться —
              скачайте обновление и откройте файл: Android предложит установить поверх.
            </Text>
            <Text size="sm" mt={4}>
              Незавершённые замеры при обновлении сохраняются: они лежат в памяти планшета.
            </Text>
            <Group gap="sm" mt="xs">
              <Button size="compact-sm" component="a" href={apkUrl}>
                Скачать обновление
              </Button>
              <Anchor size="sm" onClick={() => setDismissed(true)}>
                Напомнить позже
              </Anchor>
            </Group>
          </>
        ) : (
          <>
            <Text size="sm">
              Здесь {appVersion}, на сервере {serverVersion}. Расчёты могли поменяться —
              попросите в офисе прислать свежий APK и переустановите.
            </Text>
            <Text size="sm" mt={4}>
              Незавершённые замеры при переустановке сохраняются: они лежат в памяти планшета.
            </Text>
            <Anchor size="sm" mt={4} onClick={() => setDismissed(true)}>
              Напомнить позже
            </Anchor>
          </>
        )}
      </Alert>
    );
  }

  if (isDesktopApp()) {
    return (
      <Alert
        color="orange"
        icon={<IconRefresh size={18} />}
        radius="md"
        mb="md"
        withCloseButton
        onClose={() => setDismissed(true)}
        title="В программе на компьютере старая сборка"
      >
        <Text size="sm">
          Здесь {appVersion}, на сервере {serverVersion}. Настольная программа держит сборку
          внутри себя, и перезагрузка окна её не сменит — нужна новая версия программы:
          закройте её и поставьте свежий установщик.
        </Text>
        <Text size="sm" mt={4}>
          В браузере тот же адрес открывается уже обновлённым — если ждать некогда.
        </Text>
        <Anchor size="sm" mt={4} onClick={() => setDismissed(true)}>
          Напомнить позже
        </Anchor>
      </Alert>
    );
  }

  // Сама не смогла ни мягко, ни жёстко — последнее средство, рукой
  return (
    <Alert
      color="blue"
      icon={<IconRefresh size={18} />}
      radius="md"
      mb="md"
      withCloseButton
      onClose={() => setDismissed(true)}
      title="Программа обновилась"
    >
      <Text size="sm">
        В этой вкладке сборка {appVersion}, на сервере уже {serverVersion}, и сама она
        обновиться не смогла. Закройте вкладку и откройте программу заново.
      </Text>
      <Group gap="sm" mt="xs">
        <Button
          size="compact-sm"
          loading={updating}
          onClick={() => {
            setUpdating(true);
            void reloadToNewBuild(serverVersion ?? '', false);
          }}
        >
          Попробовать ещё раз
        </Button>
        <Anchor size="sm" onClick={() => setDismissed(true)}>
          Позже
        </Anchor>
      </Group>
    </Alert>
  );
}
